export const RESPONSIVE_RESOURCE_SLOT_SIZE = 415;
export const RESPONSIVE_LENS_PROCESS_WIDTH = 340;
export const RESPONSIVE_LENS_RESOURCE_GAP = 16;
export const WIDE_LENS_RESOURCE_SLOT_SIZE = 423;
export const IMAGE_NAVIGATION_CARD_MIN_WIDTH = 100;
export const IMAGE_NAVIGATION_CARD_MAX_WIDTH = 196;
export const IMAGE_NAVIGATION_CARD_GAP = 8;

export const getImageNavigationPreferredWidth = (imageItemCount: number) => {
  return (
    imageItemCount * IMAGE_NAVIGATION_CARD_MAX_WIDTH +
    Math.max(0, imageItemCount - 1) * IMAGE_NAVIGATION_CARD_GAP
  );
};

interface CanDisplayTextImageNavigationParams {
  availableWidth: number;
  textNavigationWidth: number;
  textNavigationCount: number;
  imageNavigationCount: number;
  moduleGap: number;
}

export const canDisplayTextImageNavigation = ({
  availableWidth,
  textNavigationWidth,
  textNavigationCount,
  imageNavigationCount,
  moduleGap,
}: CanDisplayTextImageNavigationParams) => {
  if (imageNavigationCount === 0) return false;

  const moduleCount = textNavigationCount + imageNavigationCount;
  const totalModuleGap = Math.max(0, moduleCount - 1) * moduleGap;
  const minimumImageNavigationWidth = imageNavigationCount * IMAGE_NAVIGATION_CARD_MIN_WIDTH;

  return textNavigationWidth + totalModuleGap + minimumImageNavigationWidth <= availableWidth;
};

interface CanDisplayPriorityModuleParams {
  availableWidth: number;
  higherPriorityWidth: number;
  moduleWidth: number;
}

export const canDisplayPriorityModule = ({
  availableWidth,
  higherPriorityWidth,
  moduleWidth,
}: CanDisplayPriorityModuleParams) => {
  return higherPriorityWidth + moduleWidth <= availableWidth;
};

interface CanDisplayResourceSlotsParams {
  availableWidth: number;
  higherPriorityWidth: number;
  resourceSlotCount: number;
}

export const canDisplayResourceSlots = ({
  availableWidth,
  higherPriorityWidth,
  resourceSlotCount,
}: CanDisplayResourceSlotsParams) => {
  return canDisplayPriorityModule({
    availableWidth,
    higherPriorityWidth,
    moduleWidth: resourceSlotCount * RESPONSIVE_RESOURCE_SLOT_SIZE,
  });
};

interface ResolveTextLensResourceVisibilityParams {
  availableWidth: number;
  textNavigationWidth: number;
  hasLensProcess: boolean;
  resourceSlotCount: number;
  resourceSlotSize?: number;
}

export interface TextLensResourceVisibility {
  showLensProcess: boolean;
  visibleResourceSlotCount: number;
}

export const resolveTextLensResourceVisibility = ({
  availableWidth,
  textNavigationWidth,
  hasLensProcess,
  resourceSlotCount,
  resourceSlotSize = RESPONSIVE_RESOURCE_SLOT_SIZE,
}: ResolveTextLensResourceVisibilityParams): TextLensResourceVisibility => {
  const showLensProcess =
    hasLensProcess &&
    canDisplayPriorityModule({
      availableWidth,
      higherPriorityWidth: textNavigationWidth,
      moduleWidth: RESPONSIVE_LENS_PROCESS_WIDTH,
    });
  const lensProcessWidth = showLensProcess ? RESPONSIVE_LENS_PROCESS_WIDTH : 0;
  const lensResourceGap =
    showLensProcess && resourceSlotCount > 0 ? RESPONSIVE_LENS_RESOURCE_GAP : 0;
  const resourceSlotAvailableWidth = Math.max(
    0,
    availableWidth - textNavigationWidth - lensProcessWidth - lensResourceGap,
  );

  return {
    showLensProcess,
    visibleResourceSlotCount: Math.min(
      resourceSlotCount,
      Math.floor(resourceSlotAvailableWidth / resourceSlotSize),
    ),
  };
};

interface ResolveVisibleResourceSlotCountParams {
  availableWidth: number;
  higherPriorityWidth: number;
  resourceSlotCount: number;
  resourceSlotSize?: number;
}

export const resolveVisibleResourceSlotCount = ({
  availableWidth,
  higherPriorityWidth,
  resourceSlotCount,
  resourceSlotSize = RESPONSIVE_RESOURCE_SLOT_SIZE,
}: ResolveVisibleResourceSlotCountParams) => {
  const resourceSlotAvailableWidth = Math.max(0, availableWidth - higherPriorityWidth);

  return Math.min(resourceSlotCount, Math.floor(resourceSlotAvailableWidth / resourceSlotSize));
};
